import { Component, Input } from '@angular/core';
import { Asset } from '@app/models/asset/asset';
import { Roll } from '@app/models/knitting/roll';
import { Fabric, WorkOrder } from '@app/models/order/work-order';
import { KnittingOrder } from '@app/models/production/knitting-order';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { ProductionCardBundle } from '@app/models/production/production-card/production-card-bundle';
import { ManufacturerLotNumber, ProductionCardFabricBalance } from '@app/models/production/production-card/production-card-fabric-balance';
import { EnumService } from '@app/services/enum.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { EnumValues } from '@card-software/library';

@Component({
  selector: 'app-individual-production-card-general-information',
  templateUrl: './individual-production-card-general-information.component.html',
  styleUrl: './individual-production-card-general-information.component.scss',
})
export class IndividualProductionCardGeneralInformationComponent {
  @Input() showOrderInfo = true;
  @Input() showDyeingInfo = true;
  @Input() showManufacturerTable = true;
  @Input() showBalanceTable = true;
  @Input() rolls: Roll[] = [];
  @Input() knittingOrders: KnittingOrder[] = [];
  @Input() showFabricsWithoutRolls = false;
  @Input() readOnly = false;

  fabricFormValues: EnumValues;

  constructor(
    private readonly individualProductionCardService: IndividualProductionCardService,
    private readonly enumService: EnumService,
  ) {
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};
  }

  get workOrder(): WorkOrder | null {
    return this.individualProductionCardService.workOrder;
  }

  get dyeingAsset(): Asset | null {
    return this.individualProductionCardService.dyeingAsset;
  }

  get dyeingColor(): DyeingColor | null {
    return this.individualProductionCardService.dyeingColor;
  }

  get productionCard() {
    return this.individualProductionCardService.productionCard();
  }

  get fabrics(): Fabric[] {
    return this.workOrder?.fabrics.map((woFabric) => woFabric.fabric) || [];
  }

  get bundles(): ProductionCardBundle[] {
    return this.individualProductionCardService.productionCardBundles || [];
  }

  getFabricFromWorkOrder(fabricCode: string): Fabric | null {
    return this.workOrder?.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.fabric || null;
  }

  getStrictProductionCardFabricBalance(record: unknown): ProductionCardFabricBalance {
    return record as ProductionCardFabricBalance;
  }

  getStrictManufacturerLotNumber(record: unknown): ManufacturerLotNumber {
    return record as ManufacturerLotNumber;
  }

  get clientPO(): string {
    return this.individualProductionCardService.clientPO;
  }

  get manufacturerAndLotNumber(): ManufacturerLotNumber[] {
    const manufacturerLotMap = new Map<string, { manufacturer: string; lotNumber: string; fabricCodes: Set<string>; rollCount: number }>();

    const distinctKnittingOrderReferences = this.rolls
      .map((roll) => roll.knittingOrderReference)
      .filter((ref, index, self) => ref && self.indexOf(ref) === index);

    const relatedKnittingOrders = this.knittingOrders.filter((ko) => distinctKnittingOrderReferences.includes(ko.id!));

    this.rolls.forEach((roll) => {
      const knittingOrder = relatedKnittingOrders.find((ko) => ko.id === roll.knittingOrderReference);

      if (knittingOrder) {
        const fabricDetail = knittingOrder.fabricQuantityDetails?.find((detail) => detail.variantCode === roll.knittingOrderVariantCode);

        if (fabricDetail?.yarnAndProviderInfos) {
          fabricDetail.yarnAndProviderInfos.forEach((yarnProviderInfo) => {
            const manufacturer = yarnProviderInfo.provider?.name || yarnProviderInfo.providerReference || 'Unknown';
            const lotNumber = yarnProviderInfo.providerLotNumber;

            if (lotNumber) {
              const key = `${manufacturer}_${lotNumber}`;

              if (!manufacturerLotMap.has(key)) {
                manufacturerLotMap.set(key, {
                  manufacturer,
                  lotNumber,
                  fabricCodes: new Set<string>(),
                  rollCount: 0,
                });
              }

              const entry = manufacturerLotMap.get(key)!;
              if (roll.knittingOrderVariantCode) {
                entry.fabricCodes.add(roll.knittingOrderVariantCode);
              }
              entry.rollCount++;
            }
          });
        }
      }
    });

    return Array.from(manufacturerLotMap.values()).map((entry) => ({
      manufacturer: entry.manufacturer,
      lotNumber: entry.lotNumber,
      fabricCodes: Array.from(entry.fabricCodes),
      rollCount: entry.rollCount,
    }));
  }
}
